import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * CozyOnCozy pink bundle.
 * Adds every variant in the bundle form to the cart in one request, then lets
 * the theme's cart-items-component re-render from the returned section HTML.
 * Without this script the form still posts to /cart/add as a normal form.
 */
(() => {
  if (window.__cozyPinkBundleBound) return;
  window.__cozyPinkBundleBound = true;

  // Size choice on the page's own product (e.g. Cozy Kids): swap the variant, image and prices.
  document.addEventListener('change', (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.hasAttribute('data-cozy-pink-hero-select')) return;
    const form = select.closest('[data-cozy-pink-form]');
    const option = select.selectedOptions[0];
    if (!(form instanceof HTMLFormElement) || !option) return;

    const setText = (selector, text) => {
      const el = form.querySelector(selector);
      if (el) el.textContent = text || '';
    };

    const input = form.querySelector('[data-cozy-pink-hero-input]');
    if (input instanceof HTMLInputElement) input.value = select.value;

    setText('[data-cozy-pink-hero-price]', option.dataset.price);
    setText('[data-cozy-pink-total]', option.dataset.total);
    setText('[data-cozy-pink-bundle]', option.dataset.bundle);

    const compare = form.querySelector('[data-cozy-pink-hero-compare]');
    if (compare instanceof HTMLElement) {
      compare.textContent = option.dataset.compare || '';
      compare.hidden = !option.dataset.compare;
    }

    const img = form.querySelector('[data-cozy-pink-hero-img]');
    if (img instanceof HTMLImageElement && option.dataset.img) {
      img.src = option.dataset.img;
      img.removeAttribute('srcset');
    }

    const btn = form.querySelector('[data-cozy-pink-btn]');
    const label = form.querySelector('[data-cozy-pink-label]');
    const available = option.dataset.available === 'true' && form.dataset.restAvailable === 'true';
    if (btn instanceof HTMLButtonElement) btn.disabled = !available;
    if (label instanceof HTMLElement) {
      label.textContent = (available ? label.dataset.default : label.dataset.soldout) || '';
    }
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-cozy-pink-form')) return;
    event.preventDefault();

    const btn = form.querySelector('[data-cozy-pink-btn]');
    const label = form.querySelector('[data-cozy-pink-label]');
    const error = form.querySelector('[data-cozy-pink-error]');
    if (!(btn instanceof HTMLButtonElement) || btn.disabled) return;

    // Sent in reverse so the first item on the page (the hoodie) is added last and sits at the top of the cart.
    const items = Array.from(form.querySelectorAll('input[data-cozy-pink-variant]'))
      .map((input) => ({ id: Number(input.value), quantity: 1 }))
      .filter((item) => item.id)
      .reverse();
    if (!items.length) return;

    // Refresh every cart-items-component on the page (drawer and/or cart page)
    const sections = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sections.add(el.dataset.sectionId);
    });

    form.classList.add('is-loading');
    btn.disabled = true;
    if (error instanceof HTMLElement) {
      error.hidden = true;
      error.textContent = '';
    }

    try {
      const body = JSON.stringify({
        items,
        sections: Array.from(sections).join(','),
        sections_url: window.location.pathname,
      });

      const res = await fetch(`${Theme.routes.cart_add_url}`, fetchConfig('json', { body }));
      const data = await res.json();

      if (!res.ok || data.status) {
        throw new Error(data.description || data.message || 'Could not add to cart');
      }

      if (label instanceof HTMLElement && label.dataset.added) label.textContent = label.dataset.added;

      // Need the full cart for item_count; /cart/add.js returns only the added lines.
      const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
        r.json()
      );

      document.dispatchEvent(
        new CartAddEvent(cart, 'cozy-pink-bundle', {
          source: 'cozy-pink-bundle',
          itemCount: cart.item_count,
          sections: data.sections,
        })
      );

      window.setTimeout(() => {
        if (label instanceof HTMLElement && label.dataset.default) label.textContent = label.dataset.default;
        btn.disabled = false;
      }, 2500);
    } catch (err) {
      console.error('[cozy-pink-bundle]', err);
      if (error instanceof HTMLElement) {
        error.textContent = err instanceof Error ? err.message : 'Could not add to cart';
        error.hidden = false;
      }
      btn.disabled = false;
    } finally {
      form.classList.remove('is-loading');
    }
  });
})();
